import {
  ArrowBack as BackIcon,
  History as HistoryIcon,
  OpenInNew as OpenIcon,
  Save as SaveIcon,
  Undo as DiscardIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AddPlaceDialog, {
  PlaceDialogTarget,
  PlaceSubmitResult,
} from '../components/guide-editor/AddPlaceDialog';
import ChapterDialog from '../components/guide-editor/ChapterDialog';
import { ChapterForm, FormApi, GuideForm, PickForm } from '../components/guide-editor/forms';
import GuideGateSummary from '../components/guide-editor/GuideGateSummary';
import HistoryDrawer from '../components/guide-editor/HistoryDrawer';
import OutlinePanel from '../components/guide-editor/OutlinePanel';
import {
  allPicks,
  deriveDraft,
  describeOp,
  dirtyItems,
  EditorGuide,
  enqueueRemovePick,
  enqueueRenameChapter,
  enqueueSetText,
  findChapter,
  findPick,
  GuideOp,
  opItem,
  opTargets,
  ParagraphTarget,
  QueuedOp,
  Selection,
  selectionKey,
  titleText,
  unaddressablePickIds,
  validateQueue,
} from '../components/guide-editor/model';
import { guidePreviewUrl, isPreviewOrigin } from '../components/guide-editor/urls';
import NotificationSnackbar from '../components/NotificationSnackbar';
import { useAuth } from '../contexts/GoogleAuthContext';
import { api, GuidePublishGates } from '../services/api-client';
import { guideEditorApi } from '../services/guide-editor-api';

const CONFLICT_TEXT =
  'This guide changed since you opened it. Reload to see the latest; your unsaved edits will be kept to re-apply.';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  published: 'Published',
};

interface Toast {
  message: string;
  severity: 'success' | 'info' | 'error';
}

/** Keyed by slug, so moving between two guides never carries one's queued edits into the other. */
export default function GuideEditor() {
  const { slug } = useParams<{ slug: string }>();
  return <GuideEditorScreen key={slug} />;
}

function GuideEditorScreen() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('content', 'edit');

  // The guide as the server last returned it, and the edits queued on top of it.
  const [base, setBase] = useState<EditorGuide | null>(null);
  const [queue, setQueue] = useState<QueuedOp[]>([]);
  const [gates, setGates] = useState<GuidePublishGates | null>(null);
  const [gatesError, setGatesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selection, setSelection] = useState<Selection>({ kind: 'guide' });
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [reloadedKept, setReloadedKept] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [opErrors, setOpErrors] = useState<Record<number, string>>({});
  const [serverWarnings, setServerWarnings] = useState<string[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);

  const [placeTarget, setPlaceTarget] = useState<PlaceDialogTarget | null>(null);
  const [chapterDialog, setChapterDialog] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const nextId = useRef(1);
  const newId = () => nextId.current++;

  // ─── loading ───────────────────────────────────────────────────────────────

  const fetchFresh = useCallback(async (): Promise<EditorGuide> => {
    const [guide, gateResult] = await Promise.all([
      api.getGuide(slug),
      api
        .getGuidePublishGates(slug)
        .then((g) => ({ gates: g, error: null as string | null }))
        .catch((err: any) => ({
          gates: null,
          error: (err?.message as string) || 'Failed to load the publish checklist',
        })),
    ]);
    setBase(guide as EditorGuide);
    setGates(gateResult.gates);
    setGatesError(gateResult.error);
    return guide as EditorGuide;
  }, [slug]);

  // Just the checklist (a fact-check dismissal changes it, not the guide).
  const refreshGates = useCallback(async () => {
    try {
      setGates(await api.getGuidePublishGates(slug));
      setGatesError(null);
    } catch (err: any) {
      setGatesError((err?.message as string) || 'Failed to load the publish checklist');
    }
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchFresh()
      .catch((err: any) => !cancelled && setLoadError(err?.message || 'Failed to load the guide'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [fetchFresh]);

  // ─── derived state ─────────────────────────────────────────────────────────

  const draft = useMemo(() => (base ? deriveDraft(base, queue) : null), [base, queue]);
  const clientErrors = useMemo(() => (draft ? validateQueue(draft, queue) : {}), [draft, queue]);
  const unaddressable = useMemo(() => (draft ? unaddressablePickIds(draft) : new Set<string>()), [draft]);
  const dirty = useMemo(() => dirtyItems(queue), [queue]);

  // Field key -> the problem on it: the editor's own checks, then the backend's refusal.
  const fieldErrors = useMemo(() => {
    const map: Record<string, string> = {};
    for (const q of queue) {
      const message = clientErrors[q.id] ?? opErrors[q.id];
      if (!message) continue;
      for (const key of opTargets(q.op)) if (!(key in map)) map[key] = message;
    }
    return map;
  }, [queue, clientErrors, opErrors]);

  // A selection that points at something gone (removed, or the guide reloaded) falls back.
  const current: Selection = useMemo(() => {
    if (!draft) return { kind: 'guide' };
    if (selection.kind === 'chapter' && !findChapter(draft, selection.id)) return { kind: 'guide' };
    if (selection.kind === 'pick' && !findPick(draft, selection.id)) return { kind: 'guide' };
    return selection;
  }, [draft, selection]);

  const hasClientErrors = Object.keys(clientErrors).length > 0;
  const readOnly = !canEdit || conflict;
  const status = gates?.status ?? (base?.draft === false ? 'published' : 'draft');
  const isLive = status === 'published';

  // Leaving the page with unsaved edits asks first.
  useEffect(() => {
    if (queue.length === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [queue.length]);

  // ─── queueing edits ────────────────────────────────────────────────────────

  const setText = (path: string, value: string) => {
    if (!base) return;
    setQueue((q) => enqueueSetText(base, q, path, value, newId()));
  };

  const setChapterKicker = (chapterId: string, kicker: string) => {
    if (!base || !draft) return;
    const baseKicker = findChapter(base, chapterId)?.chapter.kicker ?? '';
    setQueue((q) => {
      if (kicker === baseKicker) {
        return q.filter((x) => !(x.op.op === 'rename_chapter' && x.op.chapterId === chapterId));
      }
      const title = titleText(findChapter(deriveDraft(base, q), chapterId)?.chapter.title);
      return enqueueRenameChapter(q, { op: 'rename_chapter', chapterId, title, kicker }, newId());
    });
  };

  const push = (op: GuideOp) => setQueue((q) => [...q, { id: newId(), op }]);

  const insertParagraph = (target: ParagraphTarget, index: number) =>
    push({ op: 'insert_paragraph', ...target, index, value: '' });
  const removeParagraph = (target: ParagraphTarget, index: number) =>
    push({ op: 'remove_paragraph', ...target, index });

  const moveParagraph = (target: ParagraphTarget, from: number, to: number) =>
    push({ op: 'move_paragraph', ...target, from, to });
  const removeMeta = (pickId: string, index: number) => push({ op: 'remove_meta', pickId, index });

  const movePick = (pickId: string, chapterId: string, index: number) => {
    if (!draft) return;
    const from = findPick(draft, pickId);
    if (!from || (from.chapter.id === chapterId && from.index === index)) return;
    push({ op: 'move_pick', pickId, chapterId, index });
  };

  const removePick = (pickId: string) => {
    if (!draft) return;
    const holder = findPick(draft, pickId);
    setQueue((q) => enqueueRemovePick(q, pickId, newId()));
    setSelection(holder ? { kind: 'chapter', id: holder.chapter.id } : { kind: 'guide' });
  };

  const moveChapter = (chapterId: string, index: number) => {
    if (!draft) return;
    const from = findChapter(draft, chapterId);
    if (!from || from.index === index) return;
    push({ op: 'move_chapter', chapterId, index });
  };

  const removeChapter = (chapterId: string) => {
    push({ op: 'remove_chapter', chapterId });
    setSelection({ kind: 'guide' });
  };

  // ─── saving ────────────────────────────────────────────────────────────────

  /**
   * Send the queue (plus any `extra` operations the server must resolve, such as a
   * place) with the revision the editor read. All or nothing: a refusal writes
   * nothing and leaves the queue as it was.
   */
  const commit = async (extra: GuideOp[] = []): Promise<PlaceSubmitResult> => {
    if (!base?.revision) return { ok: false, message: 'This guide has no revision to save against.' };
    setSaving(true);
    setSaveError(null);
    const sent = queue;
    const ops = [...sent.map((q) => q.op), ...extra];
    try {
      const result = await guideEditorApi.editGuide(slug, { revision: base.revision, ops });
      const before = new Set(allPicks(base).map((p) => p.id));
      const chaptersBefore = new Set((base.chapters ?? []).map((c) => c.id));
      const fresh = await fetchFresh();
      setQueue([]);
      setOpErrors({});
      setConflict(false);
      setReloadedKept(0);
      setServerWarnings(result.warnings ?? []);
      setHistoryKey((k) => k + 1);
      const addedPick = allPicks(fresh).find((p) => !before.has(p.id));
      const addedChapter = (fresh.chapters ?? []).find((c) => !chaptersBefore.has(c.id));
      if (extra.some((o) => o.op === 'add_pick_from_place') && addedPick) {
        setSelection({ kind: 'pick', id: addedPick.id });
      } else if (extra.some((o) => o.op === 'add_chapter') && addedChapter) {
        setSelection({ kind: 'chapter', id: addedChapter.id });
      }
      setToast({
        severity: 'success',
        message: isLive
          ? 'Saved. The live page is refreshing; a reviewer must approve it again.'
          : 'Saved.',
      });
      return { ok: true };
    } catch (err: any) {
      if (err?.status === 409) {
        setConflict(true);
        return { ok: false, message: CONFLICT_TEXT };
      }
      const failed = err?.details?.failedOps as
        | { index: number; op: string | null; error: string }[]
        | undefined;
      if (err?.status === 422 && Array.isArray(failed) && failed.length > 0) {
        const errors: Record<number, string> = {};
        let extraMessage: string | undefined;
        for (const f of failed) {
          if (f.index < sent.length) errors[sent[f.index].id] = f.error;
          else extraMessage = f.error;
        }
        setOpErrors(errors);
        const queued = Object.keys(errors).length;
        const message =
          extraMessage ??
          (queued > 0
            ? `${queued} of your unsaved ${queued === 1 ? 'change' : 'changes'} could not be saved. Fix ${
                queued === 1 ? 'it' : 'them'
              } first.`
            : err.message);
        // Failed queued edits are listed (and pinned to their fields) below.
        if (extra.length === 0 && queued === 0) setSaveError(message);
        return { ok: false, message };
      }
      const message = err?.message || 'The guide could not be saved';
      if (extra.length === 0) setSaveError(message);
      return { ok: false, message };
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    void commit();
  };

  const submitServerOp = async (op: GuideOp): Promise<PlaceSubmitResult> => {
    const outcome = await commit([op]);
    if (outcome.ok) {
      setPlaceTarget(null);
      setChapterDialog(false);
    }
    return outcome;
  };

  const reload = async () => {
    setLoading(true);
    try {
      await fetchFresh();
      setConflict(false);
      setReloadedKept(queue.length);
      setOpErrors({});
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to reload the guide');
    } finally {
      setLoading(false);
    }
  };

  const discard = () => {
    setQueue([]);
    setOpErrors({});
    setSaveError(null);
    setReloadedKept(0);
    setConfirmDiscard(false);
  };

  const goBack = () => {
    if (queue.length > 0) setConfirmLeave(true);
    else navigate(`/guides/${slug}`);
  };

  // ─── render ────────────────────────────────────────────────────────────────

  if (loading && !base) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !base || !draft) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/guides/${slug}`)} sx={{ mb: 2 }}>
          Back to guide
        </Button>
        <Alert severity="error">{loadError || 'Guide not found'}</Alert>
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/guides/${slug}`)} sx={{ mb: 2 }}>
          Back to guide
        </Button>
        <Alert severity="warning">You need the content edit permission to edit guides.</Alert>
      </Box>
    );
  }

  if (!Array.isArray(base.chapters)) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/guides/${slug}`)} sx={{ mb: 2 }}>
          Back to guide
        </Button>
        <Alert severity="info">
          This is an older, flat article, not a block guide, so the guide editor cannot open it.
        </Alert>
      </Box>
    );
  }

  const formApi: FormApi = {
    draft,
    base,
    readOnly,
    unaddressable,
    setText,
    setChapterKicker,
    insertParagraph,
    removeParagraph,
    moveParagraph,
    removeMeta,
    movePick,
    removePick,
    moveChapter,
    removeChapter,
    errorFor: (key) => fieldErrors[key],
    openAddPlace: (chapterId, index) => setPlaceTarget({ mode: 'add', chapterId, index }),
    openChangePlace: (pickId) => setPlaceTarget({ mode: 'change', pickId }),
    select: setSelection,
  };

  const problems = queue.filter((q) => opErrors[q.id]);
  const title = titleText(draft.title) || slug;

  const outline = (
    <OutlinePanel
      draft={draft}
      selection={current}
      onSelect={setSelection}
      dirty={dirty}
      unaddressable={unaddressable}
      readOnly={readOnly}
      onMovePick={movePick}
      onMoveChapter={moveChapter}
      onAddPlace={(chapterId) => setPlaceTarget({ mode: 'add', chapterId })}
      onAddChapter={() => setChapterDialog(true)}
    />
  );

  return (
    <Box data-testid="guide-editor">
      {/* Top bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconButton onClick={goBack} aria-label="Back to guide">
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600} noWrap>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={STATUS_LABELS[status] ?? status}
                color={isLive ? 'success' : 'default'}
                data-testid="status-chip"
              />
              <Chip
                size="small"
                color={queue.length > 0 ? 'warning' : 'default'}
                variant={queue.length > 0 ? 'filled' : 'outlined'}
                label={`Unsaved changes: ${queue.length}`}
                data-testid="unsaved-count"
              />
              <Typography variant="caption" color="text.secondary">
                /{slug}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saving || queue.length === 0 || hasClientErrors || conflict}
              onClick={save}
              data-testid="save-button"
            >
              Save
            </Button>
            <Button
              variant="outlined"
              startIcon={<DiscardIcon />}
              disabled={saving || queue.length === 0}
              onClick={() => setConfirmDiscard(true)}
            >
              Discard
            </Button>
            <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => setHistoryOpen(true)}>
              History
            </Button>
            <Tooltip
              title={
                isPreviewOrigin()
                  ? 'An internal staging page behind SSO. A save can take a minute to show up there.'
                  : 'The guide on the site. A save can take a minute to show up there.'
              }
            >
              <Button
                variant="outlined"
                startIcon={<OpenIcon />}
                href={guidePreviewUrl(slug)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {isPreviewOrigin() ? 'Preview on staging' : 'View on site'}
              </Button>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ mt: 1 }}>
          {hasClientErrors && (
            <Typography variant="caption" color="error" component="div" data-testid="fix-fields">
              Fix the highlighted fields to save.
            </Typography>
          )}
          <GuideGateSummary
            gates={gates}
            error={gatesError}
            slug={slug}
            onGatesChanged={refreshGates}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            {isPreviewOrigin()
              ? 'Preview is internal (SSO) and may take a minute to refresh after a save. '
              : 'The site may take a minute to refresh after a save. '}
            Publishing stays with the reviewers on the guide page.
          </Typography>
        </Box>
      </Paper>

      {/* Notices */}
      {isLive && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="live-notice">
          This guide is live. Saving changes updates the live page straight away (it refreshes
          automatically). A save also voids the reviewer's approval, so a reviewer must approve and
          publish it again before the publish checklist passes.
        </Alert>
      )}
      {conflict && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="conflict-banner"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Reload
            </Button>
          }
        >
          {CONFLICT_TEXT}
        </Alert>
      )}
      {!conflict && reloadedKept > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setReloadedKept(0)}>
          Loaded the latest version. Your {reloadedKept} unsaved{' '}
          {reloadedKept === 1 ? 'change is' : 'changes are'} still queued: check{' '}
          {reloadedKept === 1 ? 'it' : 'them'} and press Save to re-apply.
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      {problems.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="failed-ops">
          Nothing was saved. The guide refused:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {problems.map((q) => (
              <li key={q.id}>
                {describeOp(q.op)}: {opErrors[q.id]}{' '}
                <Button size="small" onClick={() => setSelection(opItem(q.op, draft))}>
                  Show
                </Button>
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {serverWarnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setServerWarnings([])}>
          Saved, with notes:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {serverWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Outline + form */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: narrow ? 'column' : 'row' }}>
        {narrow ? (
          <Box sx={{ width: '100%' }}>
            <FormControl fullWidth size="small">
              <InputLabel id="outline-select">Editing</InputLabel>
              <Select
                labelId="outline-select"
                label="Editing"
                value={selectionKey(current)}
                onChange={(e) => {
                  const v = String(e.target.value);
                  setSelection(
                    v === 'guide'
                      ? { kind: 'guide' }
                      : { kind: v.startsWith('chapter:') ? 'chapter' : 'pick', id: v.slice(v.indexOf(':') + 1) }
                  );
                }}
                data-testid="outline-select"
              >
                <MenuItem value="guide">Guide</MenuItem>
                {(draft.chapters ?? []).flatMap((c) => [
                  <MenuItem key={`c-${c.id}`} value={`chapter:${c.id}`}>
                    {titleText(c.title) || c.id}
                  </MenuItem>,
                  ...(c.picks ?? []).map((p) => (
                    <MenuItem key={`p-${p.id}`} value={`pick:${p.id}`} sx={{ pl: 4 }}>
                      {titleText(p.title) || p.id}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" onClick={() => setChapterDialog(true)} disabled={readOnly}>
                Add chapter
              </Button>
            </Box>
          </Box>
        ) : (
          <Paper sx={{ width: 320, flexShrink: 0, p: 1.5, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', position: 'sticky', top: 8 }}>
            {outline}
          </Paper>
        )}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }} data-testid="editor-form">
          {current.kind === 'guide' && <GuideForm api={formApi} />}
          {current.kind === 'chapter' && <ChapterForm api={formApi} chapterId={current.id} />}
          {current.kind === 'pick' && <PickForm key={current.id} api={formApi} pickId={current.id} />}
        </Box>
      </Box>

      <AddPlaceDialog
        target={placeTarget}
        guide={draft}
        slug={slug}
        pendingCount={queue.length}
        onClose={() => setPlaceTarget(null)}
        onSubmit={submitServerOp}
      />
      <ChapterDialog
        open={chapterDialog}
        pendingCount={queue.length}
        onClose={() => setChapterDialog(false)}
        onSubmit={submitServerOp}
      />
      <HistoryDrawer
        open={historyOpen}
        slug={slug}
        reloadKey={historyKey}
        onClose={() => setHistoryOpen(false)}
      />

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {queue.length} unsaved {queue.length === 1 ? 'change' : 'changes'} will be thrown away.
            The saved guide is not touched.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={discard}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Leave without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have {queue.length} unsaved {queue.length === 1 ? 'change' : 'changes'}. They are
            lost if you leave.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={() => navigate(`/guides/${slug}`)}>
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={toast !== null}
        message={toast?.message ?? ''}
        severity={toast?.severity ?? 'success'}
        onClose={() => setToast(null)}
      />
    </Box>
  );
}
